import { Link } from 'react-router-dom'

import { Badge, Label } from 'twyne-new.ui';

import InfoTip from 'components/InfoTip';
import HierarchicalReportLayout, { formatters } from 'components/hierarchical-report/Layout'

import BucketStore from '../../stores/bucket'

import RowChartModal, { getBucketLabel, RowChartModalHost } from './components/RowChartModal'
import { BucketReportProvider, useBucketReportStore } from './store'

const STATUS_COLORS = {
  disabled: 'neutral',
  scrubing: 'danger',
  converting: 'success',
}

const CSV_HEADERS = [{
  label: 'Publisher',
  key: 'publisher',
}, {
  label: 'Allocation',
  key: 'allocation',
}, {
  label: 'Bucket',
  key: 'bucket',
}, {
  label: 'Status',
  key: 'status',
}, {
  label: 'Current Balance',
  key: 'currentBalance',
}, {
  label: 'Sessions',
  key: 'total',
}, {
  label: 'Conversions',
  key: 'converted',
}, {
  label: 'Cost',
  key: 'cost',
}, {
  label: 'Revenue',
  key: 'revenue',
}, {
  label: 'Margin',
  key: 'margin',
}]

const formatCsvMoney = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--'

  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

const formatCsvStatus = status => {
  if (!status || typeof status !== 'object') return status || '--'

  return [
    status.scrubing && `${status.scrubing} scrubing`,
    status.converting && `${status.converting} converting`,
  ].filter(Boolean).join(' / ') || '--'
}

const collectBucketRows = node => {
  if (!node) return []
  if (node.children?.length) return node.children.flatMap(collectBucketRows)
  return [node]
}

export default class BucketLayout extends HierarchicalReportLayout {
  store = BucketStore
  title = 'Bucket'

  suppressCompare = true
  suppressGroupBy = true

  useNewStore = () => {
    const store = useBucketReportStore()
    const { report: { useLoad } } = store
    useLoad()
    return store
  }

  loadData = async params => {
    const metrics = Array.from(new Set([...params.metrics || []]))
    await this.store.fetchData({ ...params, metrics })
    this.setState({})
  }

  get csv() {
    const rows = collectBucketRows(this.store.collection.data).map(row => ({
      publisher: row.publisherId ? `[#${row.publisherId}] - ${row.publisherName}` : '--',
      allocation: row.allocationId ? `[#${row.allocationId}] - ${row.allocationName}` : '--',
      bucket: getBucketLabel(row),
      status: formatCsvStatus(row.status),
      currentBalance: formatCsvMoney(row.currentBalance),
      total: row.total ?? 0,
      converted: row.converted ?? 0,
      cost: formatCsvMoney(row.cost),
      revenue: formatCsvMoney(row.revenue),
      margin: formatCsvMoney(row.margin),
    }))

    return [CSV_HEADERS, rows, rows]
  }

  configure = {
    filterby: {
      options: [{
        value: 'publisher',
        get label() {
          return <formatters.Loadable isLoading={BucketStore.publisher.isLoading}>Publisher</formatters.Loadable>
        },
        options: BucketStore.publisher.fetchData,
      }, {
        value: 'allocation',
        get label() {
          return <formatters.Loadable isLoading={BucketStore.allocation.isLoading}>Allocation</formatters.Loadable>
        },
        options: BucketStore.allocation.fetchData,
      }],
    },
    groupby: {
      default: ['bucket'],
      options: [],
    },
    sortby: {
      default: ['total desc'],
      options: [
        { value: 'total', label: 'Session', group: 'Default' },
        { value: 'revenue', label: 'Revenue', group: 'Default' },
        { value: 'cost', label: 'Cost', group: 'Default' },
        { value: 'margin', label: 'Margin', group: 'Default' },
      ],
    },
    metrics: {
      default: [],
      options: [
        { value: 'currentBalance', label: 'Current Balance', group: 'Balance' },
        { value: 'converted', label: 'Conversions', group: 'Sessions' },
        { value: 'cost', label: 'Cost', group: 'Sessions' },
        { value: 'margin', label: 'Margin', group: 'Sessions' },
        { value: 'revenue', label: 'Revenue', group: 'Sessions' },
        { value: 'sessions', label: 'Sessions', group: 'Sessions' },
      ],
    },
  }

  metricColumns = [{
    dataField: 'status',
    alwaysShow: true,
    text: <Label>Status <InfoTip side='top' color='neutral' id='status'>The current status of the bucket. Is the same value from revenue scrub tab from allocation form.</InfoTip></Label>,
    formatter: cell => {
      const color = STATUS_COLORS[cell] || 'neutral'

      if (cell === 'disabled') {
        return <Badge color={color} variant='outline'>{cell}</Badge>
      }

      return <Badge color={color} variant='outline'>{cell}</Badge>
    },
  }, {
    dataField: 'currentBalance',
    customSort: true,
    alwaysShow: true,
    sortValue: row => row.currentBalance || 0,
    text: <Label>Current Balance <InfoTip side='top' color='neutral' id='balance'>The current balance of the bucket. Is the same value from revenue scrub tab from allocation form.</InfoTip></Label>,
    formatter: formatters.parentPercent(formatters.money(), 'currentBalance'),
  }, {
    dataField: 'total',
    customSort: true,
    alwaysShow: true,
    sortValue: row => row.total || 0,
    text: <Label>Sessions</Label>,
    formatter: (cell, row) => formatters.number(cell || 0, row),
  }, {
    dataField: 'converted',
    customSort: true,
    alwaysShow: true,
    sortValue: row => row.converted || 0,
    text: <Label>Conversions</Label>,
    formatter: (cell, row) => formatters.number(cell || 0, row),
  }, {
    dataField: 'cost',
    customSort: true,
    alwaysShow: true,
    sortValue: row => row.cost || 0,
    text: <Label>Cost <InfoTip side='top' color='neutral' id='cost'>The cost of the bucket.</InfoTip></Label>,
    formatter: cell => formatters.colorize(formatters.money()(cell || 0), cell),
  }, {
    dataField: 'revenue',
    customSort: true,
    sortValue: row => row.revenue || 0,
    alwaysShow: true,
    text: <Label>Revenue <InfoTip side='top' color='neutral' id='revenue'>The revenue of the bucket.</InfoTip></Label>,
    formatter: cell => formatters.colorize(formatters.money()(cell || 0), cell),
  }, {
    dataField: 'margin',
    customSort: true,
    alwaysShow: true,
    text: <Label>Margin <InfoTip side='top' color='neutral' id='margin'>The margin of the bucket.</InfoTip></Label>,
    formatter: cell => formatters.colorize(formatters.money()(cell || 0), cell),
  }]

  nameFormatter = (cell, row) => getBucketLabel(row)

  get columns() {
    const { metrics } = this.state || {}
    const metricColumns = this.metricColumns
      .filter(d => d.alwaysShow || (metrics && metrics.includes(d.dataField)))

    return [{
      dataField: 'publisher',
      text: 'Publisher',
      customSort: true,
      sortValue: row => row.publisherId || row.publisherName,
      alwaysShow: true,
      formatter: (cell, row) => <Link to={`/offer/publishers/${row.publisherId}`} target="_blank" rel="noopener noreferrer">{`[#${row.publisherId}] - ${row.publisherName}`}</Link>,
    }, {
      dataField: 'allocation',
      text: 'Allocation',
      sortValue: row => row.allocationId || row.allocationName,
      alwaysShow: true,
      customSort: true,
      formatter: (cell, row) => <Link to={`/offer/allocations/${row.allocationId}`} target="_blank" rel="noopener noreferrer">{`[#${row.allocationId}] - ${row.allocationName}`}</Link>,
    }, {
      dataField: 'value',
      text: 'Bucket',
      alwaysShow: true,
      customSort: true,
      formatter: this.nameFormatter,
    }, ...metricColumns, {
      dataField: 'chart',
      text: '',
      formatter: (_, row) => {
        const { startDate, endDate } = this.state || {}
        return <RowChartModal row={row} startDate={startDate} endDate={endDate} />
      },
    }]
  }

  ReportProvider = ({ children }) => <BucketReportProvider>
    {children}
    <RowChartModalHost />
  </BucketReportProvider>
}
